// TwoFactorSettings.jsx
import React, { useState, useEffect } from "react";
import {
  Shield,
  Key,
  Download,
  Copy,
  Check,
  AlertCircle,
  Loader,
  X as CloseIcon,
  CheckCircle,
  RefreshCw,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const TwoFactorSettings = ({ user, showMessage }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState("");
  const [regenerateCode, setRegenerateCode] = useState("");

  useEffect(() => {
    fetch2FAStatus();
  }, []);

  const fetch2FAStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.get2FAStatus();
      if (response.success) {
        setStatus(response);
      }
    } catch (error) {
      console.error("Error fetching 2FA status:", error);
      showMessage("Failed to load 2FA status", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      setLoading(true);
      const response = await apiService.setup2FA();

      if (response.success) {
        setSetupData(response);
        showMessage("2FA setup initiated. Please scan the QR code.", "success");
      }
    } catch (error) {
      console.error("Error setting up 2FA:", error);
      showMessage(
        error.response?.data?.message || "Failed to setup 2FA",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      showMessage("Please enter a valid 6-digit code", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.verify2FA(verificationCode);

      if (response.success) {
        showMessage("2FA has been successfully enabled!", "success");
        setSetupData(null);
        setVerificationCode("");
        fetch2FAStatus();
      }
    } catch (error) {
      console.error("Error verifying 2FA:", error);
      showMessage(
        error.response?.data?.message || "Invalid verification code",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword || !disableCode) {
      showMessage("Please enter both password and verification code", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.disable2FA(
        disablePassword,
        disableCode,
      );

      if (response.success) {
        showMessage("2FA has been disabled", "success");
        setShowDisableModal(false);
        setDisablePassword("");
        setDisableCode("");
        fetch2FAStatus();
      }
    } catch (error) {
      console.error("Error disabling 2FA:", error);
      showMessage(
        error.response?.data?.message || "Failed to disable 2FA",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regeneratePassword || !regenerateCode) {
      showMessage("Please enter both password and verification code", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.regenerate2FABackupCodes(
        regeneratePassword,
        regenerateCode,
      );

      if (response.success) {
        setSetupData({ ...setupData, backupCodes: response.backupCodes });
        setShowBackupCodes(true);
        setRegeneratePassword("");
        setRegenerateCode("");
        showMessage("Backup codes regenerated successfully", "success");
      }
    } catch (error) {
      console.error("Error regenerating backup codes:", error);
      showMessage(
        error.response?.data?.message || "Failed to regenerate backup codes",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type = "secret") => {
    navigator.clipboard.writeText(text);
    if (type === "secret") {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } else {
      setCopiedCode(text);
      setTimeout(() => setCopiedCode(null), 2000);
    }
    showMessage("Copied to clipboard", "success");
  };

  const downloadBackupCodes = () => {
    if (!setupData?.backupCodes) return;

    const content = `GradeatEAM Two-Factor Authentication Backup Codes
Generated: ${new Date().toLocaleString()}

IMPORTANT: Store these codes in a secure location.
Each code can only be used once.

${setupData.backupCodes.map((code, i) => `${i + 1}. ${code}`).join("\n")}

If you lose access to your authenticator app, you can use these codes to log in.
`;

    const blob = new Blob([content], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gradeateam-backup-codes-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showMessage("Backup codes downloaded", "success");
  };

  if (loading && !status) {
    return (
      <div
        className={`rounded-lg border p-6 flex items-center justify-center min-h-[400px] ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <Loader className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
          <Shield size={20} />
        </div>
        <div>
          <h2
            className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Two-Factor Authentication
          </h2>
          <p
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Add an extra layer of security to your account
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Shield size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-400">
            <strong className="font-medium block mb-1">
              Enhanced Security with 2FA
            </strong>
            <p className="text-blue-400/80">
              Two-factor authentication adds an extra layer of security by
              requiring a code from your authenticator app in addition to your
              password.
            </p>
          </div>
        </div>
      </div>

      {/* Current Status */}
      {status && (
        <div
          className={`mb-6 p-4 rounded-lg border ${isDarkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-100 border-gray-200"}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-3 rounded-full ${
                  status.enabled ? "bg-green-500" : "bg-gray-500"
                }`}
              />
              <div>
                <div
                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {status.enabled ? "Enabled" : "Disabled"}
                </div>
                {status.enabled && (
                  <div
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {status.backupCodesRemaining} backup codes remaining
                  </div>
                )}
              </div>
            </div>
            {status.enabled && (
              <button
                onClick={() => setShowDisableModal(true)}
                className="px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors border border-red-600/30"
              >
                Disable 2FA
              </button>
            )}
          </div>
        </div>
      )}

      {/* Setup Flow */}
      {!status?.enabled && !setupData && (
        <div className="text-center py-8">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
          >
            <Shield size={32} className="text-gray-400" />
          </div>
          <h3
            className={`text-lg font-medium mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Two-Factor Authentication Not Enabled
          </h3>
          <p
            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-6 max-w-md mx-auto`}
          >
            Protect your account by enabling two-factor authentication. You'll
            need an authenticator app like Google Authenticator or Authy.
          </p>
          <button
            onClick={handleSetup2FA}
            disabled={loading}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader size={18} className="animate-spin" />
                Setting up...
              </span>
            ) : (
              "Enable 2FA"
            )}
          </button>
        </div>
      )}

      {/* Setup Instructions */}
      {setupData && !status?.enabled && (
        <div className="space-y-6">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-400 mt-0.5" />
              <div className="text-sm text-yellow-400">
                <strong className="font-medium block mb-1">Important</strong>
                <p className="text-yellow-400/80">
                  Save your backup codes in a secure location. You'll need them
                  if you lose access to your authenticator app.
                </p>
              </div>
            </div>
          </div>

          {/* Step 1: Scan QR Code */}
          <div
            className={`border rounded-lg p-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
                1
              </div>
              <h3
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Scan QR Code
              </h3>
            </div>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}
            >
              Open your authenticator app and scan this QR code:
            </p>
            <div className="flex justify-center mb-4">
              <div className="bg-white p-4 rounded-lg">
                <img
                  src={setupData.qrCode}
                  alt="2FA QR Code"
                  className="w-64 h-64"
                />
              </div>
            </div>
            <p
              className={`text-xs text-center mb-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Can't scan? Use this key instead:
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={setupData.manualEntryKey}
                readOnly
                className={`flex-1 px-3 py-2 border rounded-lg text-sm font-mono ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-gray-100 border-gray-300 text-gray-900"}`}
              />
              <button
                onClick={() =>
                  copyToClipboard(setupData.manualEntryKey, "secret")
                }
                className={`px-3 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                {copiedSecret ? (
                  <Check size={16} className="text-green-400" />
                ) : (
                  <Copy size={16} />
                )}
              </button>
            </div>
          </div>

          {/* Step 2: Verify */}
          <div
            className={`border rounded-lg p-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
                2
              </div>
              <h3
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Verify Code
              </h3>
            </div>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}
            >
              Enter the 6-digit code from your authenticator app:
            </p>
            <div className="flex gap-3">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(
                    e.target.value.replace(/\D/g, "").slice(0, 6),
                  )
                }
                className={`flex-1 px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                placeholder="000000"
                maxLength={6}
              />
              <button
                onClick={handleVerify2FA}
                disabled={loading || verificationCode.length !== 6}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader size={18} className="animate-spin" />
                ) : (
                  "Verify"
                )}
              </button>
            </div>
          </div>

          {/* Step 3: Save Backup Codes */}
          <div
            className={`border rounded-lg p-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
                3
              </div>
              <h3
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Save Backup Codes
              </h3>
            </div>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}
            >
              Store these backup codes in a secure location. Each code can only
              be used once.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {setupData.backupCodes?.map((code, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between px-4 py-3 border rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-100 border-gray-200"}`}
                >
                  <span className="font-mono text-sm">{code}</span>
                  <button
                    onClick={() => copyToClipboard(code, "code")}
                    className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
                  >
                    {copiedCode === code ? (
                      <Check size={14} className="text-green-400" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={downloadBackupCodes}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
            >
              <Download size={18} />
              Download Backup Codes
            </button>
          </div>
        </div>
      )}

      {/* Enabled State - Manage Options */}
      {status?.enabled && (
        <div className="space-y-4">
          <div
            className={`border rounded-lg p-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <h3
              className={`font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Backup Codes
            </h3>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}
            >
              You have {status.backupCodesRemaining} backup codes remaining.
              {status.backupCodesRemaining < 3 && (
                <span className="text-yellow-400 block mt-1">
                  ⚠️ You're running low on backup codes. Consider regenerating
                  them.
                </span>
              )}
            </p>

            {!showBackupCodes ? (
              <button
                onClick={() => setShowBackupCodes(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Regenerate Backup Codes
              </button>
            ) : (
              <div className="space-y-4">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={18} className="text-yellow-400 mt-0.5" />
                    <div className="text-sm text-yellow-400">
                      This will invalidate all existing backup codes and
                      generate new ones.
                    </div>
                  </div>
                </div>

                <input
                  type="password"
                  value={regeneratePassword}
                  onChange={(e) => setRegeneratePassword(e.target.value)}
                  placeholder="Enter your password"
                  className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />

                <input
                  type="text"
                  value={regenerateCode}
                  onChange={(e) =>
                    setRegenerateCode(
                      e.target.value.replace(/\D/g, "").slice(0, 6),
                    )
                  }
                  placeholder="Enter 6-digit code"
                  className={`w-full px-4 py-3 border rounded-lg text-center font-mono ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  maxLength={6}
                />

                <div className="flex gap-3">
                  <button
                    onClick={handleRegenerateBackupCodes}
                    disabled={loading}
                    className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader size={18} className="animate-spin" />
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={18} />
                        Regenerate
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowBackupCodes(false);
                      setRegeneratePassword("");
                      setRegenerateCode("");
                    }}
                    className={`px-4 py-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                  >
                    Cancel
                  </button>
                </div>

                {setupData?.backupCodes && (
                  <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle size={18} className="text-green-400" />
                      <span className="font-medium text-green-400">
                        New Backup Codes Generated
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {setupData.backupCodes.map((code, index) => (
                        <div
                          key={index}
                          className={`flex items-center justify-between px-4 py-3 border rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-100 border-gray-200"}`}
                        >
                          <span className="font-mono text-sm">{code}</span>
                          <button
                            onClick={() => copyToClipboard(code, "code")}
                            className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
                          >
                            {copiedCode === code ? (
                              <Check size={14} className="text-green-400" />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={downloadBackupCodes}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                    >
                      <Download size={18} />
                      Download Backup Codes
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Disable Two-Factor Authentication
              </h3>
              <button
                onClick={() => {
                  setShowDisableModal(false);
                  setDisablePassword("");
                  setDisableCode("");
                }}
                className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
              >
                <CloseIcon size={20} />
              </button>
            </div>

            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertCircle size={18} className="text-red-400 mt-0.5" />
                <div className="text-sm text-red-400">
                  Disabling 2FA will make your account less secure. You'll only
                  need your password to log in.
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
                className={`w-full px-4 py-3 border rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              />

              <input
                type="text"
                value={disableCode}
                onChange={(e) =>
                  setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="Enter 6-digit code"
                className={`w-full px-4 py-3 border rounded-lg text-center font-mono ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                maxLength={6}
              />

              <div className="flex gap-3">
                <button
                  onClick={handleDisable2FA}
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      Disabling...
                    </>
                  ) : (
                    "Disable 2FA"
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowDisableModal(false);
                    setDisablePassword("");
                    setDisableCode("");
                  }}
                  className={`px-4 py-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
